<template>
  <div v-loading="fetchingAPMs" class="billing-payment-cards">
    <zm-radio-group
      v-if="paymentMethodsCount > 0"
      :value="value"
      :enter-select="true"
      :aria-label="__$t('billing.payment.payment_title')"
      @input="changePaymentMethods"
      class="billing-payment-cards__group billing-payment-card-radio"
    >
      <zm-radio
        v-for="payment in paymentMethodList"
        :key="payment.id"
        :label="payment.id"
        :data-payment-id="payment.paymentMethodId ? payment.paymentMethodId : null"
        :data-payment-name="payment.paymentMethodName ? payment.paymentMethodName : null"
        :data-image-file-name="payment.tileImageFileName ? payment.tileImageFileName : null"
        ref="payMethodRef"
        class="billing-payment-cards-item"
      >
        <div class="billing-payment-cards-item__inner">
          <img
            :src="payment.iconUrl"
            class="billing-payment-cards-item__inner__icon"
            :class="payment.iconClassName"
            alt=""
          />
          <span
            v-if="payment.paymentMethodName === WISE"
            class="billing-payment-cards-item__inner__label text-capitalize wise-payment"
          >
            {{ payment.label }}
            <img src="../../assets/wise-icon.png" />
          </span>
          <span v-else class="billing-payment-cards-item__inner__label text-capitalize">{{ payment.label }}</span>
        </div>
      </zm-radio>
    </zm-radio-group>
  </div>
</template>

<script type="text/jsx">
import { find, get, isEqual } from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import { isEmptyVal } from '../../utils'
import {
APM_LOWERCASE,
  nonPayPalCurrencyList,
  payMethodClassNameMap,
  payMethodIconMap,
  payMethodMap,
  payMethodNameMap,
  payMethodRefreshKey,
  WISE,
  SUPPORTED_APM_LIST
} from '../../common/constant'
import adyenMixin from './mixins/adyen-mixin'
import { getCurrency } from '../../utils/currency'
import { translateMixins } from '../../plugins/i18n'
import { APP_NAMESPACE } from '../../store'
import { getApmPaymentMethods } from '../../apis/payment'
import { apmPaymentType } from '../../utils'
import { getDefaultPayApmInfo } from '../../store/helper'

export default {
  name: 'payment-card',
  data() {
    return {
      paymentMethods: [],
      supportPayMethodList: [
        payMethodMap.credit,
        payMethodMap.paypal,
        payMethodMap.google,
        payMethodMap.apple,
        payMethodMap.creditByIndia,
        payMethodMap.ach,
        payMethodMap.sepa
      ],
      fetchingAPMs: false,
      WISE
    }
  },
  props: {
    value: String
  },
  inject: ['isPrimary', 'isFreeAccount'],
  mixins: [adyenMixin, translateMixins],
  async mounted() {
    this.setPaymentMethods()
    if (this.eligibleForAPM) {
      await this.getApmPaymentInfo()
    }
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, [
      'billingInfoConfig',
      'isAchEligible',
      'isSepaEligible',
      'apmInfo',
      'soldToAddress',
      'billToAddress',
      'orderDataPayload',
      'isApmEnabled'
    ]),
    paymentMethodList() {
      const notSupportPaymentMethodOnOpc = this.value && this.supportPayMethodList.includes(this.value) === false
      return this.paymentMethods.filter((item) => {
        if (item.disabled || (this.billingInfoConfig.blockCCTile && item.id === 'CreditCard')) {
          return false
        } else if (isEmptyVal(this.value) || notSupportPaymentMethodOnOpc) {
          return item.key !== payMethodRefreshKey
        } else {
          return item.key === payMethodRefreshKey || this.value === item.key
        }
      })
    },
    paymentMethodsCount() {
      return this.paymentMethods.filter((item) => item.key !== payMethodRefreshKey && !item.disabled).length
    },
    eligibleForAPM() {
      return this.isPrimary && !this.isFreeAccount && this.isApmEnabled
    }
  },
  methods: {
    ...mapMutations(APP_NAMESPACE, ['setApmInfo']),
    focus() {
      this.$nextTick(() => {
        const firstPayMethodKey = get(this.paymentMethodList, '0.id')
        const firstComp = find(this.$refs?.payMethodRef, (comp) => comp?.label === firstPayMethodKey)
        firstComp?.$el?.querySelector('input[type=radio]')?.focus()
      })
    },
    setPaymentMethods() {
      this.paymentMethods = [
        {
          id: payMethodMap.credit,
          key: payMethodMap.credit,
          iconUrl: payMethodIconMap.CreditCard,
          iconClassName: payMethodClassNameMap.CreditCard,
          label: this.__$t(payMethodNameMap.CreditCard)
        },
        {
          id: payMethodMap.paypal,
          key: payMethodMap.paypal,
          iconUrl: payMethodIconMap.PayPal,
          iconClassName: payMethodClassNameMap.PayPal,
          label: this.__$t(payMethodNameMap.PayPal),
          disabled: nonPayPalCurrencyList.includes(getCurrency())
        },
        {
          id: payMethodMap.apple,
          key: payMethodMap.apple,
          iconUrl: payMethodIconMap.AdyenApplePay,
          iconClassName: payMethodClassNameMap.AdyenApplePay,
          label: this.__$t(payMethodNameMap.AdyenApplePay),
          disabled: !this.isAdyenApplePayEnabled
        },
        {
          id: payMethodMap.google,
          key: payMethodMap.google,
          iconUrl: payMethodIconMap.AdyenGooglePay,
          iconClassName: payMethodClassNameMap.AdyenGooglePay,
          label: this.__$t(payMethodNameMap.AdyenGooglePay),
          disabled: !this.isAdyenGooglePayEnabled
        },
        {
          id: payMethodMap.ach,
          key: payMethodMap.ach,
          iconUrl: payMethodIconMap.ACH,
          iconClassName: payMethodClassNameMap.ACH,
          label: this.__$t(payMethodNameMap.ACH),
          disabled: !this.isPrimary || !this.isAchEligible
        },
        {
          id: payMethodMap.sepa,
          key: payMethodMap.sepa,
          iconUrl: payMethodIconMap.SEPA,
          iconClassName: payMethodClassNameMap.SEPA,
          label: this.__$t(payMethodNameMap.SEPA),
          disabled: !this.isPrimary || !this.isSepaEligible
        },
        {
          id: payMethodRefreshKey,
          key: payMethodRefreshKey,
          iconUrl: payMethodIconMap.changeContent,
          iconClassName: 'refresh-icon',
          label: this.__$t('billing.payment.change_payment')
        }
      ]
    },
    changePaymentMethods(value) {
      if (value.includes(APM_LOWERCASE)) {
        const apmRef = find(this.$refs?.payMethodRef, (comp) => comp?.label === value)
        const apmPaymentMethodId = apmRef.$attrs['data-payment-id']
        const apmPaymentMethodName = apmRef.$attrs['data-payment-name']
        const tileImageFileName = apmRef.$attrs['data-image-file-name']
        const isPaymentSourceRequired = apmRef.$attrs['data-payments-source-required']
        const apmInfo = {
          apmPaymentMethodId,
          apmPaymentMethodName,
          tileImageFileName,
          isApm: true,
          isPaymentSourceRequired
        }
        this.setApmInfo(apmInfo)
      }
      if (value === payMethodRefreshKey && this.apmInfo.isApm) {
        if (this.apmInfo.isDefaultPayment) {
          this.setApmInfo({
            ...this.apmInfo,
            isApm: false
          })
        } else {
          this.setApmInfo(getDefaultPayApmInfo())
        }
      }
      this.$emit('change', value === payMethodRefreshKey ? '' : value)
      this.focus()
    },
    resetApmTiles() {
      this.paymentMethods = this.paymentMethods.filter((paymentMethod) => paymentMethod.id.indexOf(APM_LOWERCASE) === -1)
      this.supportPayMethodList = this.supportPayMethodList.filter(
        (supportPayMethod) => supportPayMethod.indexOf(APM_LOWERCASE) === -1
      )
    },
    showApmTile(tile) {
      return SUPPORTED_APM_LIST.includes(tile.paymentMethodName)
    },
    async getApmPaymentInfo() {
      const soldToCountry = this.soldToAddress.country
      const billToCountry = this.billToAddress.country
      const currencyCode = getCurrency()
      const email = this.soldToAddress.email
      if ([soldToCountry, billToCountry, currencyCode, email].some(isEmptyVal)) {
        return
      }
      // remove existing APM's before adding new ones
      this.resetApmTiles()
      this.fetchingAPMs = true
      // TODO: add current plans to payload
      try {
        const { result } = await getApmPaymentMethods({
          soldToCountry,
          billToCountry,
          currencyCode,
          email
        })
        const paymentTiles = result?.result || []
        paymentTiles.forEach((tile) => {
          if (this.showApmTile(tile)) {
            const apmKey = apmPaymentType(tile.paymentMethodName, tile.tileImageFileName)
            const payment = {
              id: apmKey,
              key: apmKey,
              paymentMethodId: tile.paymentMethodId,
              paymentMethodName: tile.paymentMethodName,
              tileImageFileName: tile.tileImageFileName,
              isPaymentSourceRequired: tile.paymentRequirement,
              ...this.getExtraApmPaymentInfo(tile.paymentMethodName, tile.tileImageFileName)
            }
            this.supportPayMethodList.push(apmKey)
            this.paymentMethods.splice(this.paymentMethods.length - 1, 0, payment)
          }
        })
      } catch (e) {
        console.error('error fetching APMs:', e.message)
        this.fetchingAPMs = false
      }
      this.fetchingAPMs = false
    },
    getExtraApmPaymentInfo(paymentMethodName) {
      const iconUrl = payMethodIconMap[paymentMethodName]
      const iconClassName = payMethodClassNameMap[paymentMethodName]
      const label = this.__$t(payMethodNameMap[paymentMethodName])
      return {
        iconUrl,
        iconClassName,
        label
      }
    }
  },
  watch: {
    soldToAddress: {
      async handler(val, prevVal) {
        if (!isEqual(val, prevVal)) {
          await this.getApmPaymentInfo()
          this.$emit('change', '')
          this.setApmInfo(getDefaultPayApmInfo())
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

// radio group customized to display as card style
.billing-payment-card-radio {
  $active-theme: #0b5cff;
  $padding: 16px;
  $border-color: #b8b8c4;
  $radio-focus-border-color: $active-theme;
  display: flex;
  gap: 16px;
  align-items: stretch;

  .zm-radio {
    margin-right: 0;

    &__inner {
      display: none;
    }

    &__wrap {
      height: 100%;
      padding: 0px;
    }

    &__label--inner {
      cursor: pointer;
      height: 100%;
      position: relative;
      padding: $padding;
      border: 1px solid $border-color;
      border-radius: 12px;
      min-width: 150px;
    }

    &__label {
      position: relative;

      &.is-checked::after {
        font-family: 'icozoom' !important;
        content: '\e940';
        position: absolute;
        border-radius: 8px;
        top: $padding;
        right: $padding;
        color: #0b5cff;
        line-height: 16px;
        font-size: 16px;
      }

      &.is-disabled {
        color: #9191a4;

        .zm-radio__label--inner {
          background: #f2f2f4;
          cursor: not-allowed;
        }
      }

      &:not(.is-disabled) {
        &:hover {
          .zm-radio__label--inner {
            background: #f2f2f4;
          }
        }
      }
    }
  }

  & i[check-icon]:first-child {
    // first <i/> will be check icon
    display: none;
    position: absolute;
    top: $padding;
    right: $padding + 2px;
    width: 16px;
    font-size: 16px;
    color: $active-theme;
  }

  .zm-radio__label.is-checked {
    border: none;

    .zm-radio__label--inner {
      border: 2px solid $active-theme;

      i[check-icon]:first-child {
        display: block;
      }
    }
  }

  .zm-radio:focus-within .zm-radio__label--inner {
    border-color: $radio-focus-border-color;
  }
}

.billing-payment-cards {
  &__group {
    flex-wrap: wrap;
    gap: 14px;

    @include media(mobile) {
      flex-direction: unset;
      gap: 14px;
    }

    .billing-payment-cards-item {
      .is-focus {
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px #0b5cff;
        border-radius: 10px;
      }

      &__inner {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-weight: 500;
        font-size: 14px;

        &__label {
          font-weight: 500;
          font-size: 14px;
          margin-top: 8px;
          line-height: 150%;
          white-space: normal;
          color: #222230;
          text-align: center;

          &.wise-payment {
            img {
              vertical-align: middle;
              margin-left: 3px;
            }
          }
        }

        img.credit-card {
          width: 54px;
        }

        img.paypal {
          width: 28px;
        }

        img.apple-pay {
          width: 48px;
        }

        img.google-pay {
          width: 60px;
          height: 32px;
        }

        img.sofort {
          margin-top: 5px;
        }

        img.ideal {
          margin-top: 3px;
        }

        img.bacs {
          margin-top: 10px;

          & + .wise-payment {
            margin-top: 12px;
          }
        }

        img.trustly {
          margin-top: 10px;

          & + .wise-payment {
            margin-top: 12px;
          }
        }

        img.payid {
          margin-top: 5px;

          & + .wise-payment {
            margin-top: 12px;
          }
        }

        img.poli {
          margin-top: 5px;

          & + .wise-payment {
            margin-top: 10px;
          }
        }

        img.paynow {
          margin-top: 15px;

          & + .wise-payment {
            margin-top: 12px;
          }
        }

        img.fpx {
          margin-top: 5px;
        }

        img.refresh-icon {
          width: 44px;
          height: 37px;
        }
      }
    }

    .zm-radio {
      margin-right: 0px;

      &__wrap {
        height: 100%;
        padding: 0px;
      }

      &__label {
        &--inner {
          width: 174px;

          @include media(tablet) {
            width: 100px;
          }

          height: 100%;
          min-height: 120px;
          background: #ffffff;
          border: 1px solid #b8b8c4;
          padding: 35px 17px 10px 17px;
          border-radius: 14px;
          cursor: pointer;
        }

        &.is-checked::after {
          top: 10px;
          right: 10px;
        }
      }
    }
  }

  @include media(mobile) {
    .billing-payment-cards-item {
      .zm-radio__wrap {
        .zm-radio__label--inner {
          min-height: unset;
          padding: 12px 8px;
          width: 165px;
          .billing-payment-cards-item__inner__icon {
            height: 48px;
            object-fit: contain;
          }
          .billing-payment-cards-item__inner__label {
            font-size: 14px;
            margin-top: 0;
            line-height: 120%;
          }
        }
      }
    }
  }
}
</style>
